'use client';

import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Avatar — user/clinic representation.
 *
 * Renders an image when src is provided; falls back to initials.
 * Initials are computed from `name` (first letter of first 2 tokens).
 * Background hue is derived deterministically from name for consistency.
 */

const HUES = [
  'bg-brand-100 text-brand-700',
  'bg-clay-50 text-clay-700',
  'bg-info-50 text-info-700',
  'bg-success-50 text-success-700',
  'bg-warning-50 text-warning-700',
  'bg-sand-200 text-sand-700',
];

function hashToHue(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return HUES[Math.abs(hash) % HUES.length] ?? HUES[0]!;
}

function getInitials(name: string): string {
  const tokens = name.trim().split(/\s+/).slice(0, 2);
  return tokens.map((t) => t[0]?.toUpperCase() ?? '').join('');
}

export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  src?: string;
  alt?: string;
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

export function Avatar({ src, alt, name, size = 'md', className, ...props }: AvatarProps) {
  const [errored, setErrored] = React.useState(false);
  const showImage = src && !errored;
  const fallbackBg = hashToHue(name);

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full overflow-hidden font-semibold select-none shrink-0',
        sizeClasses[size],
        !showImage && fallbackBg,
        className,
      )}
      {...props}
    >
      {showImage ? (
        <img
          src={src as string}
          alt={alt ?? name}
          className="h-full w-full object-cover"
          onError={() => setErrored(true)}
        />
      ) : (
        <span aria-hidden="true">{getInitials(name)}</span>
      )}
    </span>
  );
}
